import { Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, Subject, firstValueFrom, from, of } from "rxjs";
import { environment } from "../../../core/config/environment";
import { InspectionJobDTO } from "../models/InspectionJobDTO";
import { catchError, finalize, shareReplay, switchMap } from "rxjs/operators";
import { db, InspectionJob, JobIssueVendor, Media, SyncableStatus, UserVendorContact } from "../../../core/database/db";
import { JobIssueDTO } from "../../jobIssues/models/IssueDTO";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { JobAssetDto } from "../../media/models/JobAssetDto";
import { JobSignatureDTO } from "../models/JobSignatureDTO";
import { JobWithLot } from "../models/JobWithLot";
import { LoggerService } from "../../../shared/services/logger.service";
import JobDataService from "./job-data-service";
import { JobIssueVendorDTO } from "../../vendors/models/JobIssueVendorDTO";
import { LotDto } from "../../lots/models/LotDto";
import { AreaDTO } from "../../areas/models/AreaDTO";
import { AssertionDTO } from "../../assertions/models/AssertionDTO";
import { ComponentDTO } from "../../components/models/ComponentDTO";
import { ZoneDTO } from "../../zones/models/ZoneDTO";
import { UserVendorContactDTO } from "../../vendors/models/UserVendorContactDTO";
import { normalizeEmail } from "../../../shared/utils/email-helper";
import { VendorService } from "../../vendors/services/vendor-service";
import { UpdateSpec } from "dexie";

export interface GetConstructionManagerJobScheduleResponse {
  jobs?: JobWithLot[];
}

@Injectable({ providedIn: "root" })
export class ConstructionManagerScheduleService {

  private readonly constructionManagerScheduleBaseUrl =
    `${environment.backend.apiBaseURL}/api/JobSchedule/GetConstructionManagerJobSchedule`;

  isOnline: boolean = true;

  /** True while a schedule fetch (HTTP + local sync) is in flight, from ANY caller - since this service
   *  is a root-provided singleton, this is safe to bind directly to a loading indicator on any page. */
  readonly isFetchingSchedule = signal(false);
  private inFlightSchedule$: Observable<InspectionJob[]> | null = null;

  /** Emits once a background schedule sync has successfully written fresh data to IndexedDB, so
   *  other already-open pages (e.g. lot summary) know they should re-read their own local data. */
  public readonly scheduleRefreshed = new Subject<void>();

  constructor(private http: HttpClient,
    private logger: LoggerService,
    private networkService: NetworkStatusService,
    private vendorService: VendorService,
    private jobDataService: JobDataService,
  ) {

    this.networkService.online$.subscribe(val => {
      this.isOnline = val;
    });
  }

  private async syncIssueData(issueData: JobIssueDTO[], recoveredIssuesJobIds: number[]): Promise<void> {
    const issueDataFromApi = issueData.filter(i => !!i && !!i.identifier);

    await db.transaction("rw", [db.jobIssue, db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localIssueData = await db.jobIssue
        .where("identifier")
        .anyOf(issueDataFromApi.map(i => i.identifier))
        .toArray();

      const newIssues = issueDataFromApi.filter(apiIssue =>
        !localIssueData.find(local =>
          local.identifier === apiIssue.identifier
        )
      );
      const updateIssues: (JobIssueDTO & { id: number | undefined })[] = localIssueData.map(localIss => {
        const apiIssue = issueDataFromApi.find(api => api.identifier === localIss.identifier);

        if (apiIssue
          && localIss.syncStatus !== SyncableStatus.Pending
          && new Date(localIss.updatedAt).getTime() < new Date(apiIssue.updatedAt).getTime()
        ) {
          return {
            ...apiIssue,
            id: localIss.id,
            parentIdentifier: apiIssue.parentIdentifier ?? localIss.parentIdentifier,
          };
        }

        return null;
      }).filter(issue => !!issue);

      const apiIssueIds = new Set(issueDataFromApi.map(i => i.identifier));

      const issuesToCleanup = localIssueData.filter(localIssue =>
        localIssue.syncStatus === SyncableStatus.Completed &&
        !apiIssueIds.has(localIssue.identifier)
      );

      const localIssuesFromRecoveredJobs = await db.jobIssue
        .where("jobId")
        .anyOf(recoveredIssuesJobIds)
        .and(issue => !!issue.parentIdentifier)
        .toArray();

      issuesToCleanup.push(...localIssuesFromRecoveredJobs);

      // Add new issues
      if (newIssues.length > 0) {
        await db.batchBulkOperation("jobIssue:add", newIssues, async newIssues => {
          await db.jobIssue.bulkAdd(newIssues.map(i => ({
            ...i,
            isSelfAssigned: i.isSelfAssigned ?? false, // Ensure isSelfAssigned is always a boolean
            syncStatus: SyncableStatus.Completed,
            deleted: i.deleted === true ? 1 : 0,
          })));
        }, this.logger);
      }

      // Update existing issues
      if (updateIssues.length > 0) {
        await db.batchBulkOperation("jobIssue:update", updateIssues, async updateIssues => {
          await db.jobIssue.bulkUpdate(updateIssues.map(updateIssue => ({
            key: updateIssue.id!,
            changes: {
              ...updateIssue,
              isSelfAssigned: updateIssue.isSelfAssigned ?? false, // Ensure isSelfAssigned is always a boolean
              deleted: updateIssue.deleted === true ? 1 : 0,
            }
          })));
        }, this.logger);
      }

      // Remove stale issues
      if (issuesToCleanup.length > 0) {
        await db.batchBulkOperation("jobIssue:delete", issuesToCleanup, async issuesToCleanup => {
          await db.jobIssue.bulkDelete(issuesToCleanup.map(i => i.id!));
        }, this.logger);
      }

      db.setSuppressHooks(false);
    });

    // Sync vendor assignments for job issues
    await this.syncJobIssueVendorData(issueDataFromApi);
  }

  private async syncJobIssueVendorData(issueData: JobIssueDTO[]): Promise<void> {
    const issueIdentifiers = issueData
      .filter(issue => !!issue && !!issue.identifier)
      .map(issue => issue.identifier);

    if (issueIdentifiers.length === 0) {
      return;
    }

    // Extract all vendor assignments from the issue data
    const vendorAssignments: (JobIssueVendorDTO)[] = issueData
      .filter(issue => issue.jobIssueVendors && issue.jobIssueVendors.length > 0)
      .flatMap(issue => issue.jobIssueVendors!.map(vendor => ({
        ...vendor,
        issueIdentifier: issue.identifier
      })));

    // The read and the eventual update both happen inside this one transaction, so a concurrent local
    // vendor-assignment edit is serialized before or after this whole sync, never interleaved - no
    // separate re-read is needed right before writing.
    await db.transaction("rw", [db.jobIssueVendor, db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      try {
        // Get existing vendor assignments for all synced issues, including issues that now have zero vendors
        const existingVendors = await db.jobIssueVendor
          .where("issueIdentifier")
          .anyOf(issueIdentifiers)
          .toArray();

        // Find new vendor assignments to add
        const newVendors = vendorAssignments.filter(apiVendor =>
          !existingVendors.find(localVendor =>
            localVendor.issueVendorIdentifier === apiVendor.issueVendorIdentifier
          )
        );

        // Find vendor assignments to update
        const updateVendors: (JobIssueVendorDTO & { id: number; issueIdentifier: string })[] = existingVendors
          .map(localVendor => {
            const apiVendor = vendorAssignments.find(api =>
              api.issueVendorIdentifier === localVendor.issueVendorIdentifier
            );

            if (apiVendor
              && localVendor.syncStatus !== SyncableStatus.Pending
              && new Date(localVendor.updatedAt).getTime() < new Date(apiVendor.updatedAt).getTime()) {
              return {
                ...apiVendor,
                id: localVendor.id!,
              };
            }

            return null;
          }).filter(vendor => !!vendor);

        // Build set of API vendor identifiers per issue for cleanup, including empty sets for issues with no assignments
        const apiVendorIdsByIssue = new Map<string, Set<string>>();
        issueIdentifiers.forEach(issueIdentifier => {
          apiVendorIdsByIssue.set(issueIdentifier, new Set<string>());
        });

        vendorAssignments.forEach(vendor => {
          apiVendorIdsByIssue.get(vendor.issueIdentifier)!.add(vendor.issueVendorIdentifier);
        });

        const vendorsToCleanup = existingVendors.filter(localVendor => {
          const apiVendorIds = apiVendorIdsByIssue.get(localVendor.issueIdentifier);

          return localVendor.syncStatus === SyncableStatus.Completed &&
            !!apiVendorIds &&
            apiVendorIds.has(localVendor.issueVendorIdentifier) === false;
        });

        // Add new vendor assignments
        if (newVendors.length > 0) {
          await db.batchBulkOperation("jobIssueVendor:add", newVendors, async vendors => {
            await db.jobIssueVendor.bulkAdd(vendors.map<JobIssueVendor>(vendor => ({
              issueVendorIdentifier: vendor.issueVendorIdentifier,
              externalVendorId: vendor.externalVendorId,
              issueIdentifier: vendor.issueIdentifier,
              updatedAt: new Date(vendor.updatedAt),
              createdAt: new Date(vendor.createdAt),
              isSent: vendor.isSent || !!vendor.lastSentAt,
              viewedAt: vendor.viewedAt ? new Date(vendor.viewedAt) : undefined,
              lastSentAt: vendor.lastSentAt ? new Date(vendor.lastSentAt) : undefined,
              vendorName: vendor.vendorName,
              syncStatus: SyncableStatus.Completed
            })));
          }, this.logger);
        }

        // Update existing vendor assignments
        if (updateVendors.length > 0) {
          await db.batchBulkOperation("jobIssueVendor:update", updateVendors, async vendors => {
            await db.jobIssueVendor.bulkUpdate(vendors.map<{
              key: number;
              changes: UpdateSpec<JobIssueVendor>;
            }>(apiVendor => ({
              key: apiVendor.id!,
              changes: {
                externalVendorId: apiVendor.externalVendorId,
                updatedAt: new Date(apiVendor.updatedAt),
                createdAt: new Date(apiVendor.createdAt),
                isSent: apiVendor.isSent,
                viewedAt: apiVendor.viewedAt ? new Date(apiVendor.viewedAt) : undefined,
                lastSentAt: apiVendor.lastSentAt ? new Date(apiVendor.lastSentAt) : undefined,
                vendorName: apiVendor.vendorName,
                syncStatus: SyncableStatus.Completed
              }
            })));
          }, this.logger);
        }

        // Remove stale vendor assignments
        if (vendorsToCleanup.length > 0) {
          await db.batchBulkOperation("jobIssueVendor:delete", vendorsToCleanup, async vendors => {
            await db.jobIssueVendor.bulkDelete(vendors.map(v => v.id!));
          }, this.logger);
        }
      } finally {
        db.setSuppressHooks(false);
      }
    });
  }

  private async syncMediaData(mediaData: Media[], knownParentIdentifiers: string[] = []) {
    const mediaDataFromApi = mediaData.filter(m => !!m && !!m.fileIdentifier);

    // A parent (e.g. a lot) whose last photo was removed server-side has no entries in mediaDataFromApi
    // at all, so it must still be included here explicitly - otherwise the stale local copy on another
    // device is never even looked up below, let alone cleaned up.
    const parentIdentifiersToScan = Array.from(new Set([
      ...mediaDataFromApi.map(m => m.parentIdentifier),
      ...knownParentIdentifiers
    ]));

    await db.transaction("rw", [db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);

      const localMediaData = parentIdentifiersToScan.length > 0
        ? await db.media
          .where("parentIdentifier")
          .anyOf(parentIdentifiersToScan)
          .toArray()
        : [];

      const newMediaData = mediaDataFromApi.filter(apiMedia =>
        !localMediaData.find(local =>
          local.fileIdentifier === apiMedia.fileIdentifier
        )
      );

      const updateMediaData: (Media & { id: number })[] = localMediaData.map(localMedia => {
        const apiMedia = mediaDataFromApi.find(media => media.fileIdentifier === localMedia.fileIdentifier
        );

        if (apiMedia
          && localMedia.syncStatus !== SyncableStatus.Pending
          && new Date(localMedia.updatedAt).getTime() < new Date(apiMedia.updatedAt).getTime()
        ) {
          return {
            ...apiMedia,
            id: localMedia.id!,
          };
        }

        return null;
      }).filter(i => !!i);

      // Build set of API file identifiers for cleanup
      const apiFileIds = new Set(mediaDataFromApi.map(m => m.fileIdentifier));
      const apiParentIds = new Set(parentIdentifiersToScan);

      const mediaToCleanup = localMediaData.filter(localMedia =>
        localMedia.syncStatus === SyncableStatus.Completed &&
        apiParentIds.has(localMedia.parentIdentifier) &&
        !apiFileIds.has(localMedia.fileIdentifier)
      );

      const mediaToInsert: Media[] = newMediaData.filter(m => m.fileIdentifier && m.fileIdentifier.trim() !== "");

      // Add new media
      if (mediaToInsert.length > 0) {
        await db.batchBulkOperation("media:add", mediaToInsert, async mediaToInsert => {
          await db.media.bulkAdd(mediaToInsert);
        }, this.logger);
      }

      // Update existing media
      if (updateMediaData.length > 0) {
        await db.batchBulkOperation("media:update", updateMediaData, async updateMediaData => {
          await db.media.bulkUpdate(updateMediaData.map(updatedMedia => ({
            key: updatedMedia.id!,
            changes: {
              updatedAt: updatedMedia.updatedAt,
              imageType: updatedMedia.imageType,
              resourceUri: updatedMedia.resourceUri,
              syncStatus: SyncableStatus.Completed,
            }
          })));
        }, this.logger);
      }

      // Remove stale media
      if (mediaToCleanup.length > 0) {
        await db.batchBulkOperation("media:delete", mediaToCleanup, async mediaToCleanup => {
          await db.media.bulkDelete(mediaToCleanup.map(m => m.id!));
        }, this.logger);
      }
    });
  }

  public async syncServerData(dto: GetConstructionManagerJobScheduleResponse, userVendorContacts: UserVendorContactDTO[], shouldPerformCleanup = true): Promise<GetConstructionManagerJobScheduleResponse> {
    this.logger.info("[syncServerData] Syncing server data for Construction Manager schedule");

    const allJobs = dto.jobs ?? [];

    // Deduplicate lot data before syncing
    let lotsData = allJobs.map(job => job.lot);
    lotsData = lotsData.filter((lot, index) =>
      lotsData.findIndex(l =>
        l.id === lot.id
      ) === index
    );

    await this.jobDataService.syncLotData(
      lotsData.filter((lot): lot is LotDto => lot !== undefined)
    );

    await db.transaction("readwrite", [
      "lot",
      "inspectionJob",
      "zone",
      "area",
      "component",
      "jobIssue",
      "jobIssueVendor",
      "media",
      "jobSignature",
      "assertionIssue",
      "assertion",
      "syncStatus"
    ], async () => {

      db.setSuppressHooks(true);

      await this.jobDataService.syncJobData(allJobs.map(j => {
        const { lot: _lot, ...jobWithoutLot } = j;
        return jobWithoutLot as InspectionJobDTO;
      }));

      if (shouldPerformCleanup) {
        await this.jobDataService.cleanupStaleJobs(allJobs, true);

        const serverLotIds = new Set(lotsData.filter((lot): lot is LotDto => lot !== undefined).map(lot => lot.id));
        await this.jobDataService.cleanupStaleLots(serverLotIds);
      }

      const issueData = allJobs
        .filter(j => j.issues)
        .flatMap(j => j.issues!);

      const zoneData = lotsData
        .filter((lot): lot is LotDto => lot !== undefined)
        .flatMap(lot => lot.zones || []);
      await this.jobDataService.syncZoneData(zoneData);

      const areaData = zoneData.flatMap(z => z.areas.flatMap(a => ({ area: a, zoneId: z.id })));

      await this.jobDataService.syncAreaData(areaData);

      // Extract component data
      const componentData = allJobs.flatMap(job =>
        job.lot?.zones?.flatMap((z: unknown) => {
          const zone = z as ZoneDTO;
          return zone.areas.flatMap(a =>

            a.components.flatMap(c => ({
              component: c,
              areaIdentifier: a.identifier,
              lotId: job.lot?.id ?? 0
            }))
          );
        }) || []
      );
      const componentMetadata = await this.jobDataService.syncComponentData(componentData);

      // Extract assertion data
      const assertionData = allJobs
        .flatMap(j => {
          const zones = j.lot?.zones || [];
          return zones.flatMap((z: unknown) => {
            const zone = z as ZoneDTO;
            const areas = zone.areas || [];
            return areas.flatMap((a: AreaDTO) => {
              const components = a.components || [];
              return components.flatMap((c: ComponentDTO) => {
                const assertions = c.assertions || [];
                return assertions.map((assert: AssertionDTO) => {
                  let componentId = c.id;

                  if (!!componentId === false) {
                    componentId = componentMetadata.insertedComponents[c.identifier] || componentMetadata.existingComponents[c.identifier];
                  }

                  if (!!componentId === false) {
                    this.logger.error(`[CustomerCareManagerScheduleService:syncJobStructureData] Missing componentId for assertion ${assert.identifier} and component ${c.identifier} and job ${j.id} during sync. This assertion will not be synced.`);
                  }

                  return {
                    assertion: assert,
                    componentId,
                    lotId: j.lot?.id ?? 0,
                  };
                });
              });
            });
          });
        });
      await this.jobDataService.syncAssertionData(assertionData);

      const recoveredIssuesJobIds = allJobs
        .filter(j => j.hasRecoveredIssues === true)
        .map(j => j.id);

      await this.syncIssueData(issueData, recoveredIssuesJobIds);

      const signatureData = allJobs
        .flatMap(j => j.signatures)
        .filter((signature): signature is JobSignatureDTO =>
          signature !== undefined
        );
      if (signatureData && signatureData.length > 0) {
        await this.jobDataService.syncSignatureData(signatureData);
      }


      const normalizeMedia = (jobAsset: JobAssetDto, parentIdentifier: string): Media => ({
        uploaded: jobAsset.isUploaded ? 1 : 0,
        resourceUri: jobAsset.azureBlobUri,
        fileIdentifier: jobAsset.mediaGuid ?? "",
        updatedAt: new Date(jobAsset.updatedAt),
        parentIdentifier,
        deleted: 0,
        syncStatus: SyncableStatus.Completed,
        imageType: jobAsset.imageType
      });

      const mediaData = issueData
        .filter(a => !!a.media)
        .flatMap(i => i.media!.map(m => normalizeMedia(m, i.identifier)));

      // Lots join media via their int id (no guid identifier), unlike issues/assertions above.
      const lotMediaData = lotsData
        .filter((lot): lot is LotDto => lot !== undefined)
        .flatMap(lot => (lot.media ?? []).map(m => normalizeMedia(m, String(lot.id))));

      // Every lot or issue currently being synced is a valid cleanup target, even ones with zero media
      // right now (e.g. a lot/issue whose only photo was just removed on another device) - otherwise a
      // parent that drops to zero media never appears in mediaData at all, so its stale local copy is
      // never scanned.
      const knownMediaParentIdentifiers = [
        ...lotsData.filter((lot): lot is LotDto => lot !== undefined).map(lot => String(lot.id)),
        ...issueData.map(issue => issue.identifier)
      ];

      await this.syncMediaData([...mediaData, ...lotMediaData], knownMediaParentIdentifiers);

      db.setSuppressHooks(false);
    });

    if (userVendorContacts && userVendorContacts.length > 0) {
      await this.syncUserVendorContactData(userVendorContacts);
      await this.cleanupUserVendorContacts(userVendorContacts);
    }

    return dto;
  }

  /**
   * Reads whatever InspectionJobs are already cached locally, with no network call.
   * Used to render the schedule immediately before/while a background refresh runs.
   */
  public getConstructionManagerScheduleFromCache(): Promise<InspectionJob[]> {
    return db.inspectionJob.toArray();
  }

  public getConstructionManagerSchedule(): Observable<InspectionJob[]> {
    if (this.isOnline === false) {
      return from(db.inspectionJob.toArray());
    }

    if (this.inFlightSchedule$) {
      return this.inFlightSchedule$;
    }

    const request$ = this.http.get<GetConstructionManagerJobScheduleResponse>(
      this.constructionManagerScheduleBaseUrl
    ).pipe(
      switchMap(async dto => {
        await this.syncServerData(dto, []);

        const vendors = await this.vendorService.getVendorsForAllLots();
        const vendorContacts = vendors.flatMap(v => v.userVendorContacts ?? []);

        await this.syncUserVendorContactData(vendorContacts);
        await this.cleanupUserVendorContacts(vendorContacts);

        this.scheduleRefreshed.next();
      }),
      catchError(err => {
        this.logger.error("[getSchedule] Error syncing server data", err);
        return of(null); // swallow error so pipeline continues
      }),
      switchMap(() => from(db.inspectionJob.toArray())),
      finalize(() => {
        this.isFetchingSchedule.set(false);
        this.inFlightSchedule$ = null;
      }),
      shareReplay(1)
    );

    this.isFetchingSchedule.set(true);
    this.inFlightSchedule$ = request$;
    return request$;
  }

  public getConstructionManagerDataForSync(lotIdFilter: number[] = []): Promise<GetConstructionManagerJobScheduleResponse> {
    let url = `${environment.backend.apiBaseURL}/api/jobSchedule/GetConstructionManagerJobScheduleForDataSync`;
    if (lotIdFilter.length > 0) {
      const lotIdsQuery = lotIdFilter.map(id => `lotIds=${id}`).join("&");
      url += `?${lotIdsQuery}`;
    }
    return firstValueFrom(this.http.get<GetConstructionManagerJobScheduleResponse>(url));
  }

  async syncUserVendorContactData(contactData: UserVendorContactDTO[]): Promise<void> {
    if (contactData.length === 0) {
      return;
    }

    const uniqueContactData = Array.from(
      contactData.reduce((map, c) => {
        const key = this.getContactKey(c);
        const existing = map.get(key);
        if (!existing) {
          map.set(key, c);
        } else if (new Date(c.updatedAt).getTime() > new Date(existing.updatedAt).getTime()) {
          map.set(key, c);
        }
        return map;
      }, new Map<string, UserVendorContactDTO>()).values()
    );

    await db.transaction("rw", [db.userVendorContact, db.media, db.syncStatus], async () => {
      db.setSuppressHooks(true);
      try {
        const externalVendorIds = Array.from(new Set(
          uniqueContactData.map(contact => contact.externalVendorId)
        ));
        const externalFinancialCommunityIds = new Set(
          uniqueContactData.map(contact => contact.externalFinancialCommunityId)
        );
        const localContacts = await db.userVendorContact
          .where("externalVendorId")
          .anyOf(externalVendorIds)
          .and(contact =>
            externalFinancialCommunityIds.has(contact.externalFinancialCommunityId)
          )
          .toArray();
        const localByKey = new Map<string, UserVendorContact>(
          localContacts.map(contact => [this.getContactKey(contact), contact])
        );
        const toAdd = uniqueContactData.filter(contact =>
          !localByKey.has(this.getContactKey(contact))
        );
        const toUpdate = uniqueContactData
          .map(contact => {
            const local = localByKey.get(this.getContactKey(contact));
            if (!local || local.syncStatus === SyncableStatus.Pending) {
              return null;
            }
            const localUpdated = new Date(local.updatedAt).getTime();
            const apiUpdated = new Date(contact.updatedAt).getTime();
            return apiUpdated > localUpdated ? { api: contact, local } : null;
          })
          .filter((value): value is { api: UserVendorContactDTO; local: UserVendorContact } => !!value);

        if (toAdd.length > 0) {
          await db.userVendorContact.bulkAdd(
            toAdd.map(dto => ({
              externalVendorId: dto.externalVendorId,
              externalFinancialCommunityId: dto.externalFinancialCommunityId,
              email: dto.email,
              updatedAt: new Date(dto.updatedAt),
              deleted: dto.deleted ?? false,
              syncStatus: SyncableStatus.Completed,
            } as UserVendorContact))
          );
        }

        if (toUpdate.length > 0) {
          await db.userVendorContact.bulkUpdate(
            toUpdate.map(({ api, local }) => ({
              key: local.id!,
              changes: {
                externalVendorId: api.externalVendorId,
                externalFinancialCommunityId: api.externalFinancialCommunityId,
                email: api.email,
                updatedAt: new Date(api.updatedAt),
                deleted: api.deleted ?? local.deleted,
                syncStatus: SyncableStatus.Completed,
              }
            }))
          );
        }
      } finally {
        db.setSuppressHooks(false);
      }
    });
  }

  async cleanupUserVendorContacts(contactData: UserVendorContactDTO[]): Promise<void> {
    if (contactData.length === 0) {
      return;
    }

    const responseFinancialCommunityIds = new Set(
      contactData.map(c => c.externalFinancialCommunityId)
    );

    const allLocalContacts = await db.userVendorContact.toArray();
    const allLocalContactsForCommunities = allLocalContacts.filter(contact =>
      responseFinancialCommunityIds.has(contact.externalFinancialCommunityId)
    );

    const apiCompositeKeys = new Set(contactData.map(contact =>
      this.getContactKey(contact)
    ));

    const contactsToCleanup = allLocalContactsForCommunities.filter(localContact =>
      localContact.syncStatus === SyncableStatus.Completed &&

      !apiCompositeKeys.has(this.getContactKey(localContact))
    );

    // Remove stale contacts
    if (contactsToCleanup.length > 0) {
      await db.userVendorContact.bulkDelete(contactsToCleanup.map(c => c.id!));
    }
  }

  private getContactKey(contact: Pick<UserVendorContactDTO, "externalVendorId" | "externalFinancialCommunityId" | "email">): string {
    return `${contact.externalVendorId}|${contact.externalFinancialCommunityId}|${normalizeEmail(contact.email)}`;
  }
}